import { useCallback, useEffect, useRef, useState } from 'react';
import { ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

interface SwipeableZoomImageProps {
  images: string[];
  index: number;
  onIndexChange: (i: number) => void;
  alt?: string;
  className?: string;
  imageClassName?: string;
  showControls?: boolean;
  loop?: boolean;
  /** Render override for a given image (e.g. videos). */
  renderItem?: (src: string, i: number) => React.ReactNode;
}

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const SWIPE_THRESHOLD = 50; // px

/**
 * Smoothly slides between images with touch/mouse swipe; supports pinch-zoom,
 * wheel-zoom, double-tap zoom, and pan when zoomed in.
 */
export function SwipeableZoomImage({
  images,
  index,
  onIndexChange,
  alt = '',
  className = '',
  imageClassName = '',
  showControls = true,
  loop = true,
  renderItem,
}: SwipeableZoomImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragOffset, setDragOffset] = useState(0); // for slide preview
  const [isDragging, setIsDragging] = useState(false);
  const [animating, setAnimating] = useState(true);

  const pointers = useRef<Map<number, { x: number; y: number }>>(new Map());
  const startDist = useRef(0);
  const startScale = useRef(1);
  const startTranslate = useRef({ x: 0, y: 0 });
  const startMid = useRef({ x: 0, y: 0 });
  const swipeStart = useRef<{ x: number; y: number; t: number } | null>(null);
  const panStart = useRef<{ x: number; y: number } | null>(null);
  const lastTap = useRef(0);

  // Reset zoom when index changes
  useEffect(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
    setDragOffset(0);
    setAnimating(true);
  }, [index]);

  const go = useCallback(
    (delta: number) => {
      if (!images.length) return;
      const n = images.length;
      let next = index + delta;
      if (loop) next = (next + n) % n;
      else next = Math.max(0, Math.min(n - 1, next));
      if (next !== index) onIndexChange(next);
    },
    [images.length, index, loop, onIndexChange]
  );

  const clampTranslate = (s: number, tx: number, ty: number) => {
    const el = containerRef.current;
    if (!el) return { x: tx, y: ty };
    const w = el.clientWidth;
    const h = el.clientHeight;
    const maxX = (w * (s - 1)) / 2;
    const maxY = (h * (s - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, tx)),
      y: Math.max(-maxY, Math.min(maxY, ty)),
    };
  };

  const setZoom = (newScale: number, focal?: { x: number; y: number }) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const s = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
    let nx = translate.x;
    let ny = translate.y;
    if (focal) {
      // Keep focal point stable
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const fx = focal.x - cx;
      const fy = focal.y - cy;
      const ratio = s / scale;
      nx = fx - (fx - translate.x) * ratio;
      ny = fy - (fy - translate.y) * ratio;
    }
    if (s === 1) {
      nx = 0;
      ny = 0;
    }
    const clamped = clampTranslate(s, nx, ny);
    setScale(s);
    setTranslate(clamped);
  };

  // Pointer handlers
  const onPointerDown = (e: React.PointerEvent) => {
    (e.target as Element).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      startDist.current = Math.hypot(b.x - a.x, b.y - a.y);
      startScale.current = scale;
      startTranslate.current = { ...translate };
      startMid.current = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      swipeStart.current = null;
      panStart.current = null;
    } else if (pointers.current.size === 1) {
      if (scale > 1) {
        panStart.current = { x: e.clientX - translate.x, y: e.clientY - translate.y };
      } else {
        swipeStart.current = { x: e.clientX, y: e.clientY, t: Date.now() };
        setAnimating(false);
      }
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      const dist = Math.hypot(b.x - a.x, b.y - a.y);
      if (startDist.current > 0) {
        const ratio = dist / startDist.current;
        const newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, startScale.current * ratio));
        const clamped = clampTranslate(newScale, startTranslate.current.x, startTranslate.current.y);
        setScale(newScale);
        setTranslate(clamped);
      }
      return;
    }

    if (scale > 1 && panStart.current) {
      const nx = e.clientX - panStart.current.x;
      const ny = e.clientY - panStart.current.y;
      setTranslate(clampTranslate(scale, nx, ny));
      return;
    }

    if (swipeStart.current && scale === 1) {
      const dx = e.clientX - swipeStart.current.x;
      const dy = e.clientY - swipeStart.current.y;
      if (Math.abs(dx) > Math.abs(dy)) {
        setIsDragging(true);
        setDragOffset(dx);
      }
    }
  };

  const endPointer = (e: React.PointerEvent) => {
    pointers.current.delete(e.pointerId);

    if (pointers.current.size < 2) {
      startDist.current = 0;
    }

    if (swipeStart.current && pointers.current.size === 0) {
      const dx = e.clientX - swipeStart.current.x;
      const dt = Date.now() - swipeStart.current.t;
      setAnimating(true);
      setIsDragging(false);
      setDragOffset(0);
      if (Math.abs(dx) > SWIPE_THRESHOLD || (Math.abs(dx) > 20 && dt < 250)) {
        go(dx < 0 ? 1 : -1);
      }
      swipeStart.current = null;
    }

    if (panStart.current && pointers.current.size === 0) {
      panStart.current = null;
    }

    // Double tap zoom (single pointer)
    if (pointers.current.size === 0 && !isDragging) {
      const now = Date.now();
      if (now - lastTap.current < 300) {
        const focal = { x: e.clientX, y: e.clientY };
        if (scale > 1) setZoom(1);
        else setZoom(2.5, focal);
        lastTap.current = 0;
      } else {
        lastTap.current = now;
      }
    }
  };

  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !e.metaKey && Math.abs(e.deltaY) < 10) return;
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setZoom(scale * factor, { x: e.clientX, y: e.clientY });
  };

  // Keyboard nav
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
      else if (e.key === '+' || e.key === '=') setZoom(scale * 1.25);
      else if (e.key === '-' || e.key === '_') setZoom(scale / 1.25);
      else if (e.key === '0') setZoom(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [go, scale]);

  const n = images.length;
  const trackStyle: React.CSSProperties = {
    transform: `translate3d(calc(${-index * 100}% + ${dragOffset}px), 0, 0)`,
    transition: animating && !isDragging ? 'transform 350ms cubic-bezier(0.22, 0.61, 0.36, 1)' : 'none',
  };

  return (
    <div
      ref={containerRef}
      className={`relative w-full h-full overflow-hidden touch-none select-none ${className}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endPointer}
      onPointerCancel={endPointer}
      onWheel={onWheel}
    >
      <div className="absolute inset-0 flex" style={trackStyle}>
        {images.map((src, i) => {
          const isActive = i === index;
          const itemStyle: React.CSSProperties = isActive
            ? {
                transform: `translate3d(${translate.x}px, ${translate.y}px, 0) scale(${scale})`,
                transition: pointers.current.size > 0 ? 'none' : 'transform 200ms ease-out',
              }
            : {};
          return (
            <div key={i} className="relative w-full h-full shrink-0 flex items-center justify-center">
              <div className="w-full h-full flex items-center justify-center" style={itemStyle}>
                {renderItem ? (
                  renderItem(src, i)
                ) : (
                  <img
                    src={src}
                    alt={alt}
                    draggable={false}
                    className={`max-w-full max-h-full object-contain pointer-events-none ${imageClassName}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showControls && (
        <div className="absolute bottom-3 right-3 z-30 flex items-center gap-1 rounded-full bg-black/60 backdrop-blur-md p-1">
          <button
            type="button"
            aria-label="Zoom out"
            onClick={(e) => { e.stopPropagation(); setZoom(scale / 1.4); }}
            className="h-8 w-8 rounded-full flex items-center justify-center text-white hover:bg-white/15 disabled:opacity-40"
            disabled={scale <= MIN_SCALE}
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <span className="text-white text-xs tabular-nums px-1 min-w-[3rem] text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            type="button"
            aria-label="Zoom in"
            onClick={(e) => { e.stopPropagation(); setZoom(scale * 1.4); }}
            className="h-8 w-8 rounded-full flex items-center justify-center text-white hover:bg-white/15 disabled:opacity-40"
            disabled={scale >= MAX_SCALE}
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label="Reset zoom"
            onClick={(e) => { e.stopPropagation(); setZoom(1); }}
            className="h-8 w-8 rounded-full flex items-center justify-center text-white hover:bg-white/15 disabled:opacity-40"
            disabled={scale === 1}
          >
            <RotateCcw className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/** Simple swipe-nav hook for non-zoom hero images. Returns pointer handlers. */
export function useSwipeNav(onPrev: () => void, onNext: () => void, threshold = 50) {
  const start = useRef<{ x: number; y: number } | null>(null);
  return {
    onPointerDown: (e: React.PointerEvent) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      start.current = { x: e.clientX, y: e.clientY };
    },
    onPointerUp: (e: React.PointerEvent) => {
      if (!start.current) return;
      const dx = e.clientX - start.current.x;
      const dy = e.clientY - start.current.y;
      start.current = null;
      if (Math.abs(dx) > threshold && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) onNext();
        else onPrev();
      }
    },
    onPointerCancel: () => { start.current = null; },
  };
}
